import { useNavigate } from 'react-router-dom'

import {
  Box,
  Icon,
  Link,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { TrainingDetailFragment, TrainingWorkflowDocument } from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

const SideContent = ({ training }: { training: TrainingDetailFragment }) => {
  const { navigation } = useNavigation()
  const { company: currentCompany } = useCurrentSession()
  const navigate = useNavigate()
  const {
    categories,
    code,
    external,
    externalProvider,
    id: trainingId,
    codigoCurso,
    perfil,
    expediente,
    year,
  } = training
  const missingFundaeFields = !codigoCurso || !perfil || !expediente
  const [open, toggle] = useToggle()
  const [openCost, toggleCost] = useToggle()
  const [openSalaryCost, toggleSalaryCost] = useToggle()
  const { data } = useQuery(TrainingWorkflowDocument, {
    variables: { id: training.processId ?? '' },
    skip: !training.processId,
  })
  const {
    company: { locale, legalEntities },
  } = useCurrentSession()
  const mainLegalEntityCurrency = legalEntities.find((legalEntity) => legalEntity.main)?.currency

  const displayMoneyValue = (amount: number) => {
    return MoneyWithCurrency({
      optionalDecimals: true,
      amount: parseFloat((amount * 100).toFixed(0)),
      locale: locale || '',
      currency: isCurrency(mainLegalEntityCurrency) ? mainLegalEntityCurrency : undefined,
    })
  }

  const workflow = data?.processes.process
  return (
    <Box style={{ flex: '1' }} paddingLeft="s16" gap="s24">
      <Box gap="s8">
        {training.thumbnail && (
          <Box
            maxHeight="s140"
            maxWidth="s360"
            borderRadius={{ all: 'abs008' }}
            style={{
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={training.thumbnail}
              alt={training.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.subsidized_title')}
        </Text.Normal>
        <>
          <Text.Normal>
            {training.fundaeSubsidized &&
            currentCompany?.legalEntities?.some((entity) => entity.country === 'es')
              ? i18n.t('trainings.trainings_revamp.training_detail.overview.subsidized_by_fundae')
              : training.subsidized &&
                  currentCompany?.legalEntities?.every((entity) => entity.country !== 'es')
                ? i18n.t('trainings.trainings_revamp.training_detail.overview.subsidized')
                : i18n.t('trainings.trainings_revamp.training_detail.overview.not_subsidized')}
          </Text.Normal>
        </>
        {training.fundaeSubsidized &&
          missingFundaeFields &&
          workflow?.template?.id === 'fundae' && (
            <F0Alert
              variant="info"
              title={i18n.t(
                'trainings.trainings_revamp.training_detail.overview.fundae_missing_fields.description'
              )}
              description=""
              action={{
                label: i18n.t(
                  'trainings.trainings_revamp.training_detail.overview.fundae_missing_fields.action'
                ),
                onClick: () =>
                  navigate(
                    navigation.trainings.list.details_revamp.editTraining.view.fullPath({
                      id: trainingId,
                    }) + '?section=InternalInformation',
                    { state: { from: location.pathname } }
                  ),
              }}
            />
          )}
      </Box>
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.workflow')}
        </Text.Normal>
        <>
          {training.processId ? (
            <Link.Router
              type="solid"
              external
              to={navigation.processes.template.id.overview.fullPath({
                id: String(workflow?.id),
                category: 'training',
              })}
            >
              {workflow?.name}
            </Link.Router>
          ) : (
            <Text.Normal>
              {i18n.t('trainings.trainings_revamp.training_detail.overview.workflows_not_linked')}
            </Text.Normal>
          )}
        </>
      </Box>
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.internal_code')}
        </Text.Normal>
        <>
          <Text.Normal>{code || '-'}</Text.Normal>
        </>
      </Box>
      {external && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.provider')}
          </Text.Normal>

          <>
            <Text.Normal>{externalProvider}</Text.Normal>
          </>
        </Box>
      )}
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.tags')}
        </Text.Normal>
        <Box gap="s8" flexDirection="row" flexWrap="wrap">
          {categories.length
            ? categories.map((category) => (
                <Box key={category.id}>
                  <Tag type="grey">{category.name}</Tag>
                </Box>
              ))
            : '-'}
        </Box>
      </Box>
      <Box gap="s8">
        <Box gap="s8" flexDirection="row" alignItems="center">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.total_cost')}
          </Text.Normal>
          <Tooltip.Dialog
            label={i18n.t('trainings.trainings_revamp.training_detail.overview.total_cost_tooltip')}
            attachment="bottom right"
            open={openCost}
            toggle={toggleCost}
          >
            <Box paddingRight="s8">
              <Icon.Medium icon="info-circle-line" color="grey800" />
            </Box>
          </Tooltip.Dialog>
        </Box>
        <>
          <Text.Normal>
            {displayMoneyValue(
              parseFloat(training.totalTrainingCost) +
                parseFloat(training.totalTrainingIndirectCost)
            )}
          </Text.Normal>
        </>
      </Box>
      <Box gap="s8">
        <Box gap="s8" flexDirection="row" alignItems="center">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.total_salary_cost')}
          </Text.Normal>
          <Tooltip.Dialog
            label={i18n.t(
              'trainings.trainings_revamp.training_detail.overview.total_salary_cost_tooltip'
            )}
            attachment="bottom right"
            open={openSalaryCost}
            toggle={toggleSalaryCost}
          >
            <Box paddingRight="s8">
              <Icon.Medium icon="info-circle-line" color="grey800" />
            </Box>
          </Tooltip.Dialog>
        </Box>
        <>
          <Text.Normal>
            {displayMoneyValue(parseFloat(training.totalTrainingSalaryCost))}
          </Text.Normal>
        </>
      </Box>
      <Box gap="s8">
        <Box gap="s8" flexDirection="row" alignItems="center">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.reviewed_subsidized_cost')}
          </Text.Normal>
          <Tooltip.Dialog
            label={i18n.t(
              'trainings.trainings_revamp.training_detail.overview.total_subsidized_cost_tooltip'
            )}
            attachment="bottom right"
            open={open}
            toggle={toggle}
          >
            <Box paddingRight="s8">
              <Icon.Medium icon="info-circle-line" color="grey800" />
            </Box>
          </Tooltip.Dialog>
        </Box>
        <>
          <Text.Normal>
            {displayMoneyValue(parseFloat(training.totalTrainingSubsidizedCost))}
          </Text.Normal>
        </>
      </Box>
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.year')}
        </Text.Normal>
        <>
          <Text.Normal>{year}</Text.Normal>
        </>
      </Box>
    </Box>
  )
}

export default SideContent
